import { FileCopyOutlined } from "@mui/icons-material";
import { Stack, TablePagination } from "@mui/material";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableContainer from "@mui/material/TableContainer";
import * as React from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import useFetchPetDocuments from "../../features/pets/hooks/useFetchPetDocuments";
import { isArrayEmpty } from "../../utils/Functions";
import FullScreenMessageCore from "../fullScreenMessage/FullScreenMessageCore";
import NotFoundFullScreenMessage from "../fullScreenMessage/NotFoundFullScreenMessage";
import Skeletons from "../loader/Skeletons";
import AppointmentsWithDocumentsFilter from "./AppointmentsWithDocumentsFilter";
import FileTableHead from "./FileTableHead";
import FileTableRow from "./FileTableRow";
import { useTranslation } from "react-i18next";
import useFileViewer from "../../hooks/useFileViewer";
import FileViewerDialog from "../display/fileViewer/FileViewerDialog";
import { FILE_DOWNLOAD } from "../../enums/ApiPaths";

const minHeight = 450;

export const handlers = {
  rowsPersPage: "rows",
  page: "page",
  ord: "orderBy",
};

export const defaultSort = { orderBy: null, order: "asc" };

export default function StickyHeadTable() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);
  const { fetchData, isLoading, error } = useFetchPetDocuments();
  const { t } = useTranslation();
  const { viewFile, downloadViewerFile, closeViewer, viewerFile, isPathLoading } =
    useFileViewer();
  const sortParam = searchParams.get(handlers.ord);
  const [orderBy, order] = sortParam?.split("=") || [
    defaultSort.orderBy,
    defaultSort.order,
  ];

  const handleRequestSort = (event, property) => {
    if (searchParams.get(handlers.ord) != null) {
      searchParams.delete(handlers.ord);
    }

    const isAsc = property == orderBy && order === "asc";
    const newOrder = isAsc ? "desc" : "asc";
    const newParams = new URLSearchParams(searchParams.toString());
    newParams.set(handlers.ord, `${property}=${newOrder}`);
    setSearchParams(newParams);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    searchParams.set(handlers.page, newPage);
    setSearchParams(searchParams);
  };

  const handleChangeRowsPerPage = (event) => {
    const val = +event.target.value;
    setRowsPerPage(val);
    setPage(0);
    searchParams.set(handlers.rowsPersPage, val);
    searchParams.set(handlers.page, 0);
    setSearchParams(searchParams);
  };

  const memoized = React.useMemo(() => fetchData?.data, [fetchData]);

  if (error) {
    if (
      error?.response?.status === 403 ||
      String(error?.response?.data).toUpperCase() === String("FORBIDDEN")
    ) {
      navigate(-1);
      return;
    }
    return <NotFoundFullScreenMessage />;
  }

  return (
    <Paper
      variant="outlined"
      sx={{ borderRadius: 3, borderColor: "divider", overflow: "hidden" }}
    >
      <AppointmentsWithDocumentsFilter />
      {isArrayEmpty(fetchData.data) && !isLoading ? (
        <Stack sx={{ minHeight: minHeight }}>
          <FullScreenMessageCore
            withoutWrapper={false}
            shouldCenter={false}
            centerRelative={true}
            containerBoxShadow={undefined}
            title={"NoFilesFound"}
            icon={<FileCopyOutlined className="Mui-fullscreen-icon" />}
            containerPadding={3}
          />
        </Stack>
      ) : (
        <TableContainer
          sx={{
            height: minHeight,
          }}
        >
          {isLoading ? (
            <Skeletons height={50} grid={false} numOfSkeletons={7} />
          ) : (
            <Table
              sx={{
                minWidth: minHeight,
                maxHeight: minHeight,
                overflow: "scroll",
              }}
            >
              <FileTableHead
                order={order}
                orderBy={orderBy}
                onRequestSort={handleRequestSort}
              />
              <TableBody>
                {memoized.map((data, index) => {
                  const path = `${FILE_DOWNLOAD}${data.fileDetails.id}`;
                  return (
                    <FileTableRow
                      data={data}
                      index={index}
                      key={index}
                      isViewLoading={isPathLoading(path)}
                      onView={() =>
                        viewFile({ path, name: data.fileDetails.name })
                      }
                    />
                  );
                })}
              </TableBody>
            </Table>
          )}
        </TableContainer>
      )}

      <TablePagination
        rowsPerPageOptions={[5, 10, 25]}
        component="div"
        labelRowsPerPage={t("RowsPerPage")}
        count={fetchData.count || 0}
        rowsPerPage={searchParams.get(handlers.rowsPersPage) || 10}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        backIconButtonProps={{
          title: t("PreviousPage"),
          "aria-label": t("PreviousPage"),
        }}
        nextIconButtonProps={{
          title: t("NextPage"),
          "aria-label": t("NextPage"),
        }}
      />
      <FileViewerDialog
        file={viewerFile}
        onClose={closeViewer}
        onDownload={downloadViewerFile}
      />
    </Paper>
  );
}
